import { BrowserInfo } from 'Interfaces/SupportTickets/supportTicketInterfaces';

/**
 * Parse user agent for browser name, version, OS, and OS version (inline, no dependency)
 */
function parseUserAgent(ua: string): {
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
} {
  let browser = 'Unknown';
  let browserVersion = '';
  let os = 'Unknown';
  let osVersion = '';

  // Browser (order matters: Edge contains Chrome, etc.)
  const edgeMatch = ua.match(/Edg(?:e|A|iOS)?\/(\d+[\d.]*)/);
  const chromeMatch = ua.match(/Chrome\/(\d+[\d.]*)/);
  const firefoxMatch = ua.match(/Firefox\/(\d+[\d.]*)/);
  const safariMatch = ua.match(/Version\/(\d+[\d.]*).*Safari/);
  const msieMatch = ua.match(/(?:MSIE |Trident\/.*rv:)(\d+[\d.]*)/);

  if (edgeMatch) {
    browser = 'Edge';
    browserVersion = edgeMatch[1];
  } else if (chromeMatch && !ua.includes('Edg')) {
    browser = 'Chrome';
    browserVersion = chromeMatch[1];
  } else if (firefoxMatch) {
    browser = 'Firefox';
    browserVersion = firefoxMatch[1];
  } else if (safariMatch && !ua.includes('Chrome')) {
    browser = 'Safari';
    browserVersion = safariMatch[1];
  } else if (msieMatch) {
    browser = 'IE';
    browserVersion = msieMatch[1];
  }

  // OS
  if (ua.includes('Windows NT 10') || ua.includes('Windows NT 11')) {
    os = 'Windows';
    osVersion = ua.includes('Windows NT 11') ? '11' : '10/11';
  } else if (ua.includes('Windows')) {
    os = 'Windows';
    const winMatch = ua.match(/Windows NT ([\d.]+)/);
    osVersion = winMatch ? winMatch[1] : '';
  } else if (ua.includes('Mac OS X')) {
    os = 'macOS';
    const macMatch = ua.match(/Mac OS X ([\d_]+)/);
    osVersion = macMatch ? macMatch[1].replace(/_/g, '.') : '';
  } else if (ua.includes('Android')) {
    os = 'Android';
    const andMatch = ua.match(/Android ([\d.]+)/);
    osVersion = andMatch ? andMatch[1] : '';
  } else if (ua.includes('iPhone') || ua.includes('iPad')) {
    os = 'iOS';
    const iosMatch = ua.match(/OS ([\d_]+)/);
    osVersion = iosMatch ? iosMatch[1].replace(/_/g, '.') : '';
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  }

  return { browser, browserVersion, os, osVersion };
}

/**
 * Infer device type from UA and screen
 */
function getDeviceType(): 'Mobile' | 'Tablet' | 'Desktop' {
  const ua = navigator.userAgent.toLowerCase();
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const width = window.screen.width;

  if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua) && !/ipad|tablet/i.test(ua)) {
    return 'Mobile';
  }
  if (/ipad|tablet|playbook|silk/i.test(ua) || (hasTouch && width >= 768 && width <= 1024)) {
    return 'Tablet';
  }
  return 'Desktop';
}

/**
 * Format byte size as human-readable string (e.g. "0.91 KB")
 */
function formatStorageSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  return `${kb.toFixed(2)} KB`;
}

/**
 * Capture comprehensive ticket data including browser info and context
 * @param customContext - Optional custom context to include (userRole, sessionActive, userId, environment, etc.)
 * @param includeScreenshot - Whether to capture screenshot (requires html2canvas)
 * @returns Object with browser info and additional context
 */
export const captureComprehensiveTicketData = async (
  customContext?: Record<string, any>,
  includeScreenshot: boolean = false
): Promise<{
  browserInfo: BrowserInfo;
  additionalContext: Record<string, any>;
  screenshot?: string;
}> => {
  const browserInfo = getBrowserInfo();
  const additionalContext = getAdditionalContext(customContext);

  let screenshot: string | undefined;
  if (includeScreenshot) {
    screenshot = undefined;
  }

  return {
    browserInfo,
    additionalContext,
    screenshot
  };
};

/**
 * Get browser information including parsed UA, timezone, device type
 */
export const getBrowserInfo = (): BrowserInfo => {
  const now = new Date();
  const ua = navigator.userAgent;
  const { browser, browserVersion, os, osVersion } = parseUserAgent(ua);
  const timezone =
    typeof Intl !== 'undefined' && Intl.DateTimeFormat
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : undefined;

  return {
    userAgent: ua,
    platform: navigator.platform,
    language: navigator.language,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    url: window.location.href,
    timestamp: now.toISOString(),
    browser,
    browserVersion,
    os,
    osVersion,
    deviceType: getDeviceType(),
    timezone
  };
};

/**
 * Get performance metrics with keys: pageLoadTime, domContentLoaded, firstPaint, memoryUsage
 */
export const getPerformanceMetrics = (): Record<string, any> => {
  const out: Record<string, any> = {};

  if (typeof window === 'undefined' || !window.performance) return out;

  const timing = (window.performance as Performance & { timing?: PerformanceTiming }).timing;
  if (timing) {
    out.pageLoadTime = timing.loadEventEnd > 0 ? timing.loadEventEnd - timing.navigationStart : undefined;
    out.domContentLoaded =
      timing.domContentLoadedEventEnd > 0
        ? timing.domContentLoadedEventEnd - timing.navigationStart
        : undefined;
  }

  try {
    const paintEntries = window.performance.getEntriesByType?.('paint');
    const firstPaint = paintEntries?.find((e: PerformanceEntry & { name?: string }) => e.name === 'first-paint');
    if (firstPaint && 'startTime' in firstPaint) {
      out.firstPaint = Math.round(firstPaint.startTime);
    }
  } catch {
    // ignore
  }

  const perf = window.performance as Performance & { memory?: { usedJSHeapSize: number } };
  if (perf.memory?.usedJSHeapSize) {
    out.memoryUsage = Math.round(perf.memory.usedJSHeapSize / 1024 / 1024); // MB
  }

  return out;
};

/**
 * Get network information with onlineStatus, connectionType, effectiveType, downlink, rtt
 */
export const getNetworkInfo = (): Record<string, any> => {
  const conn =
    (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection;

  const info: Record<string, any> = {
    onlineStatus: navigator.onLine,
    connectionType: conn?.type ?? 'unknown',
    effectiveType: conn?.effectiveType ?? 'unknown',
    downlink: conn?.downlink,
    rtt: conn?.rtt
  };
  return info;
};

/**
 * Get additional context (performance, network, storage sizes, page info, custom context)
 */
export const getAdditionalContext = (
  customContext?: Record<string, any>
): Record<string, any> => {
  const context: Record<string, any> = {
    currentUrl: typeof window !== 'undefined' ? window.location.href : '',
    referrer: typeof document !== 'undefined' ? (document.referrer || 'N/A') : 'N/A',
    pageTitle: typeof document !== 'undefined' ? document.title : ''
  };

  if (typeof window !== 'undefined' && window.performance) {
    const metrics = getPerformanceMetrics();
    if (Object.keys(metrics).length > 0) {
      context.performanceMetrics = metrics;
    }
  }

  if ('connection' in navigator) {
    context.networkInfo = getNetworkInfo();
  }

  try {
    const localSize = new Blob(Object.values(localStorage)).size;
    context.localStorage = formatStorageSize(localSize);
  } catch {
    context.localStorage = 'unavailable';
  }

  try {
    const sessionSize = new Blob(Object.values(sessionStorage)).size;
    context.sessionStorage = formatStorageSize(sessionSize);
  } catch {
    context.sessionStorage = 'unavailable';
  }

  // Merge custom context (userRole, sessionActive, userId, environment, etc.) so they appear in Additional Context
  if (customContext && typeof customContext === 'object') {
    Object.assign(context, customContext);
  }

  return context;
};

/**
 * Capture screenshot (requires html2canvas)
 * Stubbed for now - can be implemented when html2canvas is added
 */
export const captureScreenshot = async (): Promise<string | undefined> => {
  console.warn('Screenshot capture not implemented - html2canvas required');
  return undefined;
};

/**
 * Get error stack trace if available
 */
export const getErrorStack = (error?: Error): string | undefined => {
  if (!error) return undefined;
  return error.stack;
};
